import React, { FC, useEffect, useState } from 'react';
import { BlueButton, Box, Button } from '~Styles';
import Icon from '~ComponentsImproved/Icon';
import PrintIcon from '~Assets/icons/print.svg';
import { useButton, useDispatch, useI18n, usePrintBill } from '~Hooks';
import * as actions from '~actions';
import { OrderWithItems } from '~Hooks/useOrderParking';
import DividerWithText from '~Components/DividerWithText';
import ReceiptIcon from '~/Assets/icons/receipt_long.svg';
import CheckIcon from '~/Assets/icons/check.svg';
import { useTableApi } from '~Components/TableUniverse';
import ModalWrapper from '~ComponentsImproved/Modal/ModalWrapper';

interface Props<Type = {}> {
  chequeId: string;
  order: OrderWithItems & Type;
  onContinue: () => void;
}

/** Shows a modal before parking a cheque with options to print cheque and/or bong */
const ParkedOrderPrintsModal: FC<Props> = ({ chequeId, order, onContinue }) => {
  const { i18n } = useI18n();
  const dispatch = useDispatch();
  const tableApi = useTableApi();
  const printBill = usePrintBill({
    selectedCheque: chequeId,
    /** The following will result in Table: -- Guests: -- in the bill */
    selectedTable: { id: '', name: '' },
  });

  const [hasBillPrinted, setHasBillPrinted] = useState<boolean>(false);
  const [hasKitchenTicketPrinted, setHasKitchenTicketPrinted] = useState<boolean>(false);

  const printBillButton = useButton({
    onClick: () => {
      printBill.printBill();
      setHasBillPrinted(true);
    },
    disabled: hasBillPrinted,
  });

  const printKitchenTicketButton = useButton({
    onClick: () => {
      dispatch({
        type: actions.SEND_TABLE_ITEMS,
        items: order.items.filter((item) => !item.isMessage),
        order: {
          ...order,
          orderNumber: order.orderEasyId,
          estimatedFinishTime: order.desiredTime || null,
        },
        serviceType: order.serviceType!,
        messages: order.items.filter((item) => item.isMessage),
        origin: order.origin,
        skipConfirmation: true, // Skip askToPrintBong confirmation since Spain modal already asked
      });

      tableApi.markPrinted(chequeId);

      setHasKitchenTicketPrinted(true);
    },
    disabled: hasKitchenTicketPrinted,
  });

  const continueButton = useButton({
    onClick: () => {
      onContinue();
    },
  });

  useEffect(() => {
    if (hasBillPrinted && hasKitchenTicketPrinted) {
      setTimeout(() => {
        onContinue();
      }, 100);
    }
  }, [hasBillPrinted, hasKitchenTicketPrinted, onContinue]);

  return (
    <ModalWrapper
      header={{ title: i18n('ParkedOrders.BongOrChequePrompt'), icon: PrintIcon }}
      padding="3xl">
      <Box fluid display="flex" gap="large" marginTop="medium">
        <Button display="flex" alignItems="center" gap="xs" fluid {...printBillButton}>
          {i18n('TM.Bill')}
          <Icon src={hasBillPrinted ? CheckIcon : ReceiptIcon} color="var(--colorBlueGray600)" />
        </Button>
        <Button display="flex" alignItems="center" gap="xs" fluid {...printKitchenTicketButton}>
          {i18n('PrintType.BONG')}
          <Icon
            src={hasKitchenTicketPrinted ? CheckIcon : ReceiptIcon}
            color="var(--colorBlueGray600)"
          />
        </Button>
      </Box>

      <DividerWithText />

      <BlueButton fluid {...continueButton}>
        {i18n('App.Continue')}
      </BlueButton>
    </ModalWrapper>
  );
};

export default ParkedOrderPrintsModal;
